import { RefObject, useState, useEffect } from 'react';

import TextField from '@mui/material/TextField';

import { useDebounce } from '../../hooks';
import { Box } from '../../Box';
import { Button } from '../../Button';
import { Icon } from '../../Icon';

import { useStyles } from './GlobalSearchInput.styles';
import { GlobalSearchInputSize } from './GlobalSearchInput.types';

const DEBOUNCE_TIMEOUT = 200;
const MAX_LENGTH = 255;

/**
 * Свойства компонента {@link GlobalSearchInput}
 */
export interface GlobalSearchInputProps {
  /**
   * Текущее значение поля ввода поиска.
   */
  value: string;

  /**
   * Опциональный плейсхолдер для поля ввода поиска.
   */
  placeholder?: string;

  /**
   * Функция обратного вызова, которая вызывается при изменении значения поиска.
   */
  onChange: (value: string) => void;

  /**
   * Размер компонента.
   *
   * @defaultValue `'medium'`
   */
  size?: 'medium' | 'large';

  /**
   * Ref для доступа к input элементу.
   */
  inputRef?: RefObject<HTMLInputElement | null>;

  /**
   * Тестовый идентификатор для поля ввода поиска.
   */
  testId?: string;
}

/**
 * Компонент поиска, который предоставляет поле ввода с опциональными иконками и обработкой немедленных изменений.
 *
 * @public
 * @param value - Текущее значение поля ввода поиска.
 * @param placeholder - Опциональный плейсхолдер для поля ввода поиска.
 * @param onChange - Функция обратного вызова, которая вызывается при изменении значения поиска.
 * @param size - Размер компонента.
 * @param inputRef - Ref для доступа к input элементу.
 * @param testId - Тестовый идентификатор для поля ввода поиска.
 * @returns Отрендеренный компонент поиска.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link GlobalSearchInputProps}
 */
export const GlobalSearchInput = ({
  value,
  onChange,
  placeholder,
  size = 'medium',
  inputRef,
  testId = 'global-search-input-form-test-id',
}: GlobalSearchInputProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const { classes } = useStyles({
    size: GlobalSearchInputSize[size],
  });

  const debouncedInputValue = useDebounce(currentValue, DEBOUNCE_TIMEOUT);
  const isMaxLengthReached = currentValue.length >= MAX_LENGTH;

  const getHelperText = () => {
    if (isMaxLengthReached) {
      return 'Введено максимальное количество символов для поискового запроса: 255';
    }
    return '';
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    onChange(debouncedInputValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  return (
    <TextField
      inputRef={inputRef}
      data-testid={testId}
      value={currentValue}
      onChange={(event) => {
        const newValue = event.target.value;
        if (newValue.length <= MAX_LENGTH) {
          setCurrentValue(newValue);
        }
      }}
      helperText={getHelperText()}
      variant="outlined"
      fullWidth
      slotProps={{
        input: {
          classes: {
            root: classes.globalSearchInputRoot,
            input: classes.globalSearchInput,
            notchedOutline: classes.notchedOutline,
          },
          startAdornment: (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              px="8"
              position="absolute"
              left={0}
              width="56px"
              height="40px"
            >
              <Icon name="search" size="24" color="text.light.tertirary" />
            </Box>
          ),
          endAdornment: currentValue && (
            <Box px="8" position="absolute" right={0}>
              <Button
                size="medium"
                variant="tertiaryLight"
                onClick={() => {
                  onChange('');
                  setCurrentValue('');
                }}
                testId="cancel-button-test-id"
                icon={<Icon name="dismiss_overlay" size="24" />}
              />
            </Box>
          ),
        },
        formHelperText: {
          classes: {
            root: classes.helperText,
          },
        },
      }}
      size="small"
      placeholder={placeholder || ''}
    />
  );
};
